import { Controller } from '@hotwired/stimulus';

/**
 * Turbo Confirm Controller
 *
 * Generic controller for actions that require confirmation before making a Turbo Stream request.
 * Useful for lock/unlock, delete, or any destructive action that updates the UI via Turbo Streams.
 *
 * Usage:
 * ```html
 * <button data-controller="turbo-confirm"
 *         data-turbo-confirm-url-value="/path/to/endpoint"
 *         data-turbo-confirm-method-value="PUT"
 *         data-turbo-confirm-message-value="Are you sure you want to lock this version?"
 *         data-action="click->turbo-confirm#execute">
 *   Lock
 * </button>
 * ```
 *
 * Values:
 * - url: The endpoint URL to send the request to (required)
 * - method: HTTP method (PUT, DELETE, POST). Defaults to POST
 * - message: Confirmation message. Defaults to "Are you sure?"
 * - responseFormat: "turbo" (default) for Turbo Stream responses, "json" for JSON API responses (reloads page on success)
 * - body: Optional JSON string to send as the request body
 */
export default class extends Controller {
  static values = {
    url: String,
    method: { type: String, default: 'POST' },
    message: { type: String, default: 'Are you sure?' },
    responseFormat: { type: String, default: 'turbo' },
    body: { type: String, default: '' }
  };

  execute(event) {
    event.preventDefault();

    if (!confirm(this.messageValue)) {
      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const acceptHeader = this.responseFormatValue === 'json'
      ? 'application/json'
      : 'text/vnd.turbo-stream.html';

    const headers = {
      Accept: acceptHeader,
      'X-CSRF-Token': csrfToken
    };

    const fetchOptions = {
      method: this.methodValue,
      headers,
      credentials: 'same-origin'
    };

    if (this.bodyValue) {
      headers['Content-Type'] = 'application/json';
      fetchOptions.body = this.bodyValue;
    }

    fetch(this.urlValue, fetchOptions)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          return response.json().then((data) => ({ type: 'json', data }));
        }
        return response.text().then((html) => ({ type: 'turbo', html }));
      })
      .then((result) => {
        if (result.type === 'json') {
          window.location.reload();
        } else {
          Turbo.renderStreamMessage(result.html);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Turbo confirm request failed:', error);
        alert('An error occurred. Please try again.');
      });
  }
}
